package br.com.texeratx.boardgametimeapp.ui.screens.expansions.create

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Save
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.core.image.LocalImagePreview
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateExpansionUI(
    state: CreateExpansionState,
    snackbarHost: @Composable () -> Unit,
    onBack: () -> Unit,
    onNameChange: (String) -> Unit,
    onBggUrlChange: (String) -> Unit,
    onGameSelected: (Long) -> Unit,
    onPickImage: () -> Unit,
    onClearImage: () -> Unit,
    onSave: () -> Unit,
    onDismissError: () -> Unit
) {
    val S = LocalStrings.current
    var gamesMenuExpanded by remember { mutableStateOf(false) }

    if (state.errorMessage != null) {
        AlertDialog(
            onDismissRequest = onDismissError,
            confirmButton = {
                Button(onClick = onDismissError) { Text(S.ok) }
            },
            title = { Text(S.oops) },
            text = { Text(state.errorMessage ?: "") }
        )
    }

    AppScreenScaffold(
        snackbarHost = snackbarHost,
        topBar = {
            TopAppBar(
                title = { Text(S.createExpansionTitle) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                actions = {
                    IconButton(
                        onClick = onSave,
                        enabled = !state.isSaving && !state.isLoadingGames && !state.isUploadingImage
                    ) {
                        Icon(Icons.Default.Save, contentDescription = S.save)
                    }
                },
                colors = AppTopBarColors()
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {

            if (state.isLoadingGames) {
                CircularProgressIndicator()
                return@Column
            }

            // Dropdown de jogos (pré-selecionado)
            val selectedGameName = state.games.firstOrNull { it.id == state.selectedGameId }?.name
                ?: (state.selectedGameId?.toString() ?: S.selectGame)

            ExposedDropdownMenuBox(
                expanded = gamesMenuExpanded,
                onExpandedChange = { gamesMenuExpanded = it }
            ) {
                OutlinedTextField(
                    value = selectedGameName,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(S.labelGame) },
                    trailingIcon = {
                        ExposedDropdownMenuDefaults.TrailingIcon(expanded = gamesMenuExpanded)
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .menuAnchor()
                )

                DropdownMenu(
                    expanded = gamesMenuExpanded,
                    onDismissRequest = { gamesMenuExpanded = false }
                ) {
                    state.games.forEach { g ->
                        DropdownMenuItem(
                            text = { Text(g.name) },
                            onClick = {
                                onGameSelected(g.id)
                                gamesMenuExpanded = false
                            }
                        )
                    }
                }
            }

            OutlinedTextField(
                value = state.name,
                onValueChange = onNameChange,
                label = { Text(S.labelExpansionName) },
                modifier = Modifier.fillMaxWidth()
            )

            // --- IMAGEM (LOCAL) ---
            Text(
                text = S.sectionExpansionImage,
                style = MaterialTheme.typography.titleMedium
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedButton(
                    onClick = onPickImage,
                    enabled = !state.isSaving && !state.isUploadingImage
                ) {
                    Text(S.selectImage)
                }

                OutlinedButton(
                    onClick = onClearImage,
                    enabled = state.selectedImageName != null && !state.isSaving && !state.isUploadingImage
                ) {
                    Text(S.remove)
                }

                if (state.isUploadingImage) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(22.dp),
                        strokeWidth = 2.dp
                    )
                }
            }

            state.localImageBytes?.let { bytes ->
                LocalImagePreview(
                    bytes = bytes,
                    contentDescription = S.previewExpansionImage,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(180.dp)
                        .clip(RoundedCornerShape(16.dp))
                )
            }

            OutlinedTextField(
                value = state.bggUrl,
                onValueChange = onBggUrlChange,
                label = { Text(S.labelBggUrlOptional) },
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(Modifier.height(8.dp))

            Button(
                onClick = onSave,
                enabled = !state.isSaving && !state.isUploadingImage,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(16.dp)
            ) {
                if (state.isSaving) {
                    CircularProgressIndicator()
                } else {
                    Text(S.saveExpansion)
                }
            }
        }
    }
}
